import { Form, Input, Switch, Card} from '@nackle/paper';
import isEmpty from 'lodash/isEmpty';
import React, { Component, ReactNode } from 'react';
import { CardHeader } from 'shared/card-header';
import { HasPermission } from 'shared/HasPermission';
import { danger, ValidationMapOf } from 'shared/helper-text-helpers';
import { Cabinet, CabinetEntry, Room } from 'shared/interfaces';
import SaveButton from 'shared/save-button';
import { setRoomValue } from '../../actions';
import CabinetSelector from '../cabinet-selector';

interface ArcadeRoomProps {
    // readonly elevation?: Elevation;
    readonly room: Room;
    readonly setReorderedCabinets: (cabinets: CabinetEntry[]) => unknown;
    readonly createArcadeRoom: (room: Room) => Promise<unknown>;
    readonly updateArcadeRoom: (room: Room, id: string) => Promise<unknown>;
    readonly setRoomValue: typeof setRoomValue;
    readonly getArcadeCabinets: (arcadeId?: string) => void;
    readonly cabinetOptions: Array<Cabinet>;
    readonly addCabinetToRoom: (cabinet: CabinetEntry) => unknown;
    readonly removeRoomCabinet: (cabinetId: string) => unknown;
}

interface State {
    validationErrors: ValidationMapOf<Room>;
}

function validateRoom(room: Room): ValidationMapOf<Room> {
    const ret: ValidationMapOf<Room> = {};

    if (!room.name) {
        ret.name = danger('This field is required');
    }

    return ret;
}

export default class ArcadeRoom extends Component<ArcadeRoomProps> {

    state: State = {
        validationErrors: {},
    };

    private usingCustomRoomName = false;

    constructor(props) {
        super(props);
        props.getArcadeCabinets();
    }

    componentDidMount = () => {
        const {
            room: {
                name,
                cabinets,
                freePlay
            }
        } = this.props;

        // Attempts to determine if a custom room name is being used or if it
        // is the auto-generated name for an instant play.
        this.usingCustomRoomName = (freePlay && cabinets.length > 0 && cabinets[0])
            ? (name !== this.formatInstantPlayRoomName(cabinets[0].name))
            : (name !== '');
    };

    render() {
        const {
            room: {
                id,
                freePlay,
                name
            },
            cabinetOptions,
            getArcadeCabinets
        } = this.props;
        const { validationErrors } = this.state;

        let saveButton: ReactNode;
        const roomExists = Boolean(id);
        if (roomExists) {
            saveButton = (
                <HasPermission app="arcade-api" permission="edit-room">
                    {(hasPermission: boolean) =>
                        <SaveButton
                            disabled={!hasPermission}
                            id="saveArcadeRoomButton"
                            save={this.saveArcadeRoom}
                            successMessage="Room updated successfully"
                        >
                            Save
                        </SaveButton>
                    }
                </HasPermission>
            );
        } else {
            saveButton = (
                <SaveButton
                    id="saveArcadeRoomButton"
                    save={this.saveArcadeRoom}
                    successMessage="Room created successfully"
                >
                    Create
                </SaveButton>
            );
        }

        return (
            <Card>
                <CardHeader
                    right={saveButton}
                    title="Room Configuration"
                    className="room-configuration-title"
                />
                <Form layout="vertical">
                    {/* Room name input field */}
                    <Form.Item
                        label='Room Name (non-localizable)'
                        {...validationErrors.name}
                        style={{display: 'block', paddingBottom: 15}}
                    >
                        <Input
                            type='text'
                            placeholder='Name to appear in Arcade Studio'
                            name='name'
                            value={ name }
                            onChange={ this.handleRoomNameChange }
                        />
                    </Form.Item>
                    {/* Room options section containing toggle for instant play */}
                    <Form.Item
                        style={{display: 'block', paddingBottom: 15}}
                        label="Room Options"
                    >
                        <Switch
                            // className={ Classes.FIXED }
                            checked={ freePlay }
                            title='Instant Play'
                            onChange={ this.handleInstantPlayToggle}
                        />
                        <div style={{ display: 'inline', paddingLeft: 10 }}>Instant Play</div>
                    </Form.Item>

                    {/* Cabinet selection section */}
                    <Form.Item
                        style={{display: 'block'}}
                        label={freePlay ? 'Select a Cabinet' : 'Add and Remove Cabinets'}
                    >
                        { freePlay &&
                            <CabinetSelector
                                room={ this.props.room }
                                cabinetOptions={ cabinetOptions }
                                roomActions={{
                                    fetchLatestCabinets: getArcadeCabinets,
                                    setReorderedCabinets: this.props.setReorderedCabinets,
                                    addCabinetToRoom: this.setInstantPlay,
                                    removeCabinetFromRoom: this.props.removeRoomCabinet
                                }}
                                noCabinetsText={ 'No instant play selected' }
                                selectedHeaderText={ 'Instant Play' }
                            />
                        }

                        { !freePlay &&
                            <CabinetSelector
                                room={ this.props.room }
                                cabinetOptions={ cabinetOptions }
                                roomActions={{
                                    fetchLatestCabinets: getArcadeCabinets,
                                    setReorderedCabinets: this.props.setReorderedCabinets,
                                    addCabinetToRoom: this.addCabinet,
                                    removeCabinetFromRoom: this.props.removeRoomCabinet
                                }}
                                selectedHeaderText={ 'Added' }
                            />
                        }
                    </Form.Item>
                </Form>
            </Card>
        );
    }

    /**
     * The provided cabinet will be added to the room.
     * @param cabinet - The cabinet to add.
     */
    private addCabinet = (cabinet: Cabinet) => {
        return this.props.addCabinetToRoom(cabinet);
    };

    /**
     * Removes all cabinets that are in the room.
     */
    private clearCabinets = () => {
        const { room: { cabinets } } = this.props;
        for (let i = cabinets.length - 1; i >= 0; i--) {
            this.props.removeRoomCabinet(cabinets[i].cabinetId);
        }
    };

    /**
     * Clears the room while before adding a single cabinet. This is to be
     * used when trying to set a cabinet for the INSTANT PLAY type.
     * @param cabinet - The cabinet to add after removing all other cabinets.
     */
    private setInstantPlay = (cabinet: Cabinet) => {
        if (cabinet) {
            this.clearCabinets();
            this.addCabinet(cabinet);
            this.props.setReorderedCabinets([...this.props.room.cabinets, cabinet]);

            // Set the room name equal to the cabinet name (if not using custom)
            if (!this.usingCustomRoomName) {
                this.handleRoomValue('name', this.formatInstantPlayRoomName(cabinet.name));
                // this.clearError(name);
            }
        }
        return {
            type: 'test',
            cabinet: {} as CabinetEntry
        };
    };

    /**
     * Converts a cabinet into a representative string in a format defined for
     * instant plays.
     * @param cabinetName - The cabinet to reference when creating the name/label.
     */
    private formatInstantPlayRoomName = (cabinetName: string) => `${cabinetName} (Instant Play)`;

    /**
     * Called when the room name input field has been edited.
     * @param event - The event the handler receives.
     */
    private handleRoomNameChange = (event) => {
        const { name, value } = event.target;

        this.handleRoomValue(name, value);
        this.clearError(name);

        // If the value of the room name is edited by the user, set a flag
        this.usingCustomRoomName = (value !== '');
    };

    /**
     * Called when instant play switch state is changed
     */
    private handleInstantPlayToggle = () => {
        const { room: { freePlay } } = this.props;
        this.clearCabinets();
        this.handleRoomValue('freePlay', !freePlay);
    };


    /**
     * Used to set some data in a room given a name of the property and the value
     * to set.
     */
    private handleRoomValue = (name, value) => this.props.setRoomValue(name, value);

    /**
     * Triggers the attempt at saving the arcade room. If there are errors present,
     * it will fail, else, the room will be updated with the latest data.
     */
    private saveArcadeRoom = async () => {
        const { room } = this.props;

        this.setState({ validationErrors: {} });
        const validationErrors = validateRoom(room);
        if (!isEmpty(validationErrors)) {
            this.setState({ validationErrors });
            throw new Error('One or more validation errors prevented submitting the form. ' +
                'Please ensure all fields are filled-out correctly.');
        }

        if (room.id) {
            await this.props.updateArcadeRoom(room, room.id);
        } else {
            await this.props.createArcadeRoom(room);
        }
    };

    /**
     * Removes a set of errors that are under the name `name`.
     * @param key - The group of errors to be cleared.
     */
    private clearError = (key: string) => {
        if (this.state.validationErrors[key]) {
            this.setState((previous: State) => {
                const validationErrors = { ...previous.validationErrors };
                delete validationErrors[key];
                return { ...previous, validationErrors };
            });
        }
    };

}
